import TextFieldsIcon from "@mui/icons-material/TextFields";
import { Skeleton } from "@mui/material";
import hljs from "highlight.js";
import React, { useEffect, useRef, useState } from "react";

import { useAppState } from "../../hooks/useAppState";
import { useHighlightJsTheme } from "../../hooks/useHighlightJsTheme";
import { env } from "../../util/env";
import { sanitize } from "../../util/html";
import {
  BookContainer,
  HeaderContainer,
  HeaderLeftContainer,
  HeaderRightContainer,
  StyledBodyTypography,
  StyledButton,
  StyledPopover,
  StyledTypography,
} from "./Coursebook.elements";
import { useBook } from "./Coursebook.queries";
import { FontSlider } from "./FontSlider";

export function isPreviewer(getPathname = () => location.pathname): boolean {
  return getPathname().includes("/previewer");
}

export function modifyImageURLs(text: Document, moduleId?: string): Document {
  text.querySelectorAll("img").forEach(img => {
    const src = img.getAttribute("src");
    if (src && !/^https?:\/\//i.test(src)) {
      const baseUrl = env.VITE_BASE_IMG_URL;
      const normalizedSrc = src.startsWith("/") ? src : `/${src}`;
      img.src = `https://${baseUrl}/${moduleId}/notes${normalizedSrc}`;
    }
  });
  return text;
}

export const attachAnchorScrollHandler = (container: HTMLElement): (() => void) => {
  const anchors = container.querySelectorAll("a[href^='#']");
  const handleAnchorClick = (event: Event) => {
    event.preventDefault();
    const anchor = event.currentTarget as HTMLAnchorElement;
    const href = anchor.getAttribute("href");
    if (href && href.startsWith("#")) {
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  anchors.forEach(a => {
    a.addEventListener("click", handleAnchorClick);
  });

  return () => {
    anchors.forEach(a => {
      a.removeEventListener("click", handleAnchorClick);
    });
  };
};

export const applyExternalLinkAttributes = (container: Document | HTMLElement): void => {
  const anchors = container.querySelectorAll("a");
  anchors.forEach(a => {
    const href = a.getAttribute("href");
    if (href && !href.startsWith("#")) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    }
  });
};

export function modifyAnchorLinksForModal(text: Document): Document {
  text.querySelectorAll("[id]").forEach(el => {
    const currentId = el.getAttribute("id");
    if (currentId && !currentId.startsWith("modal-")) {
      el.setAttribute("id", `modal-${currentId}`);
    }
  });

  text.querySelectorAll("a[href^='#']").forEach(anchor => {
    const href = anchor.getAttribute("href");
    if (href && href !== "#" && !href.startsWith("#modal-")) {
      anchor.setAttribute("href", `#modal-${href.substring(1)}`);
    }
  });

  const ariaAttributes = [
    "aria-labelledby",
    "aria-describedby",
    "aria-controls",
    "aria-owns",
    "aria-activedescendant",
    "aria-details",
  ];

  ariaAttributes.forEach(attr => {
    text.querySelectorAll(`[${attr}]`).forEach(el => {
      const attrValue = el.getAttribute(attr);
      if (attrValue) {
        const newValue = attrValue
          .split(/\s+/)
          .map(id => (id.startsWith("modal-") ? id : `modal-${id}`))
          .join(" ");
        el.setAttribute(attr, newValue);
      }
    });
  });

  return text;
}

export const applySyntaxHighlighting = (ref: React.RefObject<HTMLDivElement>) => {
  if (ref.current) {
    const codeBlocks = ref.current.querySelectorAll(
      "pre[class*='language-'] code, pre code[class*='language-'], pre code.highlight",
    );
    codeBlocks.forEach(block => {
      const codeContent = block.innerHTML.replaceAll(/<br\s*\/?>/gi, "\n");
      block.textContent = codeContent;
      hljs.highlightElement(block as HTMLElement);
    });
  }
};

export function Coursebook({
  titleSize,
  fontSize,
  handleChangeFontSize,
  customControl,
  isModal = false,
}: {
  titleSize: number;
  fontSize: number;
  handleChangeFontSize(fontSize: number): void;
  customControl: React.ReactElement;
  isModal?: boolean;
}): React.ReactElement | null {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { bookText, loading } = useBook();
  const open = Boolean(anchorEl);
  const { slide, section } = useAppState();

  useHighlightJsTheme();

  const text = new DOMParser().parseFromString(bookText, "text/html");

  if (!isPreviewer()) {
    modifyImageURLs(text, slide?.moduleId);
  }

  applyExternalLinkAttributes(text);

  text.querySelectorAll("div").forEach(div => {
    const hasCodeBlocks = div.querySelectorAll(':scope > p[style*="background"]').length;
    const isIndented = div.querySelectorAll('p[style*="margin-left:1.0in"]');

    isIndented.forEach(el => el.classList.add("indented"));
    if (hasCodeBlocks) {
      div.classList.add("codeBlock");
    }
  });

  text.querySelectorAll("table").forEach(table => {
    const parent = table.parentNode as HTMLElement;
    const wrapper = document.createElement("div");

    wrapper.style.overflowX = "auto";
    wrapper.classList.add("tableWrapper");

    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });

  if (isModal) {
    modifyAnchorLinksForModal(text);
  }

  const bookTextWithMedia = new XMLSerializer().serializeToString(text);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const evalCopy =
    '<span>If the course evaluation did not open, please go to <a href="' +
    section?.surveyUrl +
    '" target="_blank" rel="noreferrer">' +
    section?.surveyUrl +
    "</a> to complete the course evaluation.</span>";

  const coursebookBeginRef = useRef<null | HTMLDivElement>(null);

  const scrollToTop = () => {
    const top = window.pageYOffset || document.documentElement.scrollTop;
    const left = window.pageXOffset || document.documentElement.scrollLeft;
    coursebookBeginRef.current?.scrollIntoView();
    window.scrollTo(top, left);
  };

  useEffect(() => {
    scrollToTop();
  }, [bookTextWithMedia]);

  useEffect(() => {
    if (!loading) {
      applySyntaxHighlighting(coursebookBeginRef);
    }
  }, [bookTextWithMedia, loading]);

  useEffect(() => {
    if (coursebookBeginRef.current) {
      return attachAnchorScrollHandler(coursebookBeginRef.current);
    }
  }, [bookTextWithMedia]);

  return (
    <BookContainer data-testid="coursebook" ref={coursebookBeginRef}>
      {loading ? (
        <>
          <Skeleton variant="rectangular" width={"80%"} height={30} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={"100%"} height={408} sx={{ mt: 2, borderRadius: 1 }} />
        </>
      ) : (
        <>
          <HeaderContainer isModal={isModal}>
            <HeaderLeftContainer>
              <StyledButton aria-label="Adjust font size" onClick={handleClick}>
                <TextFieldsIcon />
              </StyledButton>
              <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                role="tooltip"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <FontSlider handleChangeFontSize={handleChangeFontSize} fontSize={fontSize} />
              </StyledPopover>
            </HeaderLeftContainer>
            <HeaderRightContainer>{customControl}</HeaderRightContainer>
          </HeaderContainer>
          {slide?.fullNumber && slide?.name && (
            <StyledTypography component="h3" fontSize={fontSize + titleSize}>
              {slide?.fullNumber}: {slide?.name}
            </StyledTypography>
          )}
          {bookTextWithMedia ? (
            <StyledBodyTypography
              component="div"
              fontSize={fontSize}
              dangerouslySetInnerHTML={{
                __html: sanitize(bookTextWithMedia, { ADD_ATTR: ["target"] }),
              }}
            />
          ) : (
            <>
              <StyledBodyTypography
                component="div"
                fontSize={fontSize}
                dangerouslySetInnerHTML={{
                  __html: sanitize(
                    "<span>If your browser allows pop-ups for sans.org, the course evaluation should open " +
                      "automatically in a new window or tab when this slide loads.</span>",
                  ),
                }}
              />
              <StyledBodyTypography
                component="div"
                fontSize={fontSize}
                dangerouslySetInnerHTML={{
                  __html: sanitize(evalCopy),
                }}
              />
            </>
          )}
        </>
      )}
    </BookContainer>
  );
}
